
import React, { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Divider,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SaveIcon from "@mui/icons-material/Save";

import HeaderMenu from "../Header/HeaderMenu";
import useGuestManage from "./useGuestManage";
import { emptyRow } from "./constants";
import GuestEntryCard from "./GuestEntryCard";
import DeleteDialog from "./DeleteDialog";

const createBlankRow = () => ({
  ...emptyRow,
  isSaved: false,
  isEditing: true,
  finalBill: 0,
  finalBillManual: false,
  dailyMessDetails: [],
  guestDetails: [],
  stayRateManual: false,
  propertyType: "",
  stayDurationMinutes: null,
  stayDurationHours: null,
  microStayCategory: "",
  checkIn: "",
  checkOut: "",
  errorMsg: "",
  successMsg: "",
});

const SingleBill = ({
  showHeader = true,
  compact = false,
  initialGuestDetails = null,
  mode = "create",
  occupancyEditType = null,
  onEdit,
  onSaved,
}) => {
  const guest = useGuestManage({ singleBill: true });

  const [localError, setLocalError] = useState("");
  const [currentMode, setCurrentMode] = useState(mode);

  const isExisting = Boolean(initialGuestDetails);
  const isCreating = currentMode === "create" || !isExisting;
  const isViewing = isExisting && currentMode === "view";
  const isEditing = isExisting && currentMode === "edit";

  const selectedHostelObj = (guest.hostelList || []).find(
    (hostel) =>
      String(hostel.id) === String(guest.selectedHostel) ||
      hostel.hostelName === guest.selectedHostel
  );

  const hostelId = selectedHostelObj?.id;

  // Reset mode when the selected record changes.
  useEffect(() => {
  setCurrentMode(
    isExisting
      ? occupancyEditType
        ? "edit"
        : mode
      : "create"
  );
}, [mode, initialGuestDetails, occupancyEditType, isExisting]);

  // Load the fetched record into the guest form.
  // Adjust the mapping if your API returns a nested object.
  useEffect(() => {
    if (!initialGuestDetails) {
      guest.setRows([]);
      return;
    }

    const record =
      initialGuestDetails.shortStayMeal ??
      initialGuestDetails.shortStayMealDetails ??
      initialGuestDetails;

    const recordHostel =
      record.hostelName ??
      record.hostel?.hostelName ??
      record.hostelId ??
      record.hostel?.id;

    if (recordHostel != null) {
      guest.setSelectedHostel(recordHostel);
    }

    // This mapping assumes the API record matches the row shape
    // used by GuestEntryCard. Update it to match your response fields.
    guest.setRows([
      {
        ...createBlankRow(),
        ...record,
        id:
          record.id ??
          record.shortStayMealId ??
          record.stayId,
		   name: record.customerName ?? "",
        isSaved: true,
        isEditing: false,
      },
    ]);
    // The record is intentionally used as the effect's source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialGuestDetails]);


const isOccupancyEdit = Boolean(occupancyEditType);

const isDateOnlyEdit =
  occupancyEditType === "dates";

const isFullOccupancyEdit =
  occupancyEditType === "all";
  
  const handleHostelChange = (event) => {
    if (isExisting) return;

    guest.setSelectedHostel(event.target.value);
    guest.setRows([]);
    guest.setErrorMsg("");
    guest.setSuccessMsg("");
    setLocalError("");
  };

  const startNewBill = () => {
    if (!guest.selectedHostel) {
      setLocalError("Please select a hostel first.");
      return;
    }

    setLocalError("");
    guest.setErrorMsg("");
    guest.setSuccessMsg("");
    guest.setRows([createBlankRow()]);
  };

  const handleEdit = () => {
    setCurrentMode("edit");

    guest.setRows((rows) =>
      rows.map((row) => ({
        ...row,
        isEditing: true,
      }))
    );

    onEdit?.();
  };

const handleSave = async (rowOrIndex) => {
  const index =
    typeof rowOrIndex === "number"
      ? rowOrIndex
      : guest.rows.findIndex(
          (item) =>
            item === rowOrIndex ||
            String(item.id) === String(rowOrIndex?.id)
        );

  if (index < 0 || index >= guest.rows.length) return;

  const saved = await guest.handleSaveRow(index);

  if (saved === true) {
    onSaved?.();
  }
};

  return (
    <>
      {showHeader && <HeaderMenu />}

      <Box
        sx={{
          minHeight: compact ? "auto" : "100vh",
          background: compact
            ? "transparent"
            : "linear-gradient(180deg, #f5f8fc 0%, #eef2f7 100%)",
          py: compact ? 0 : { xs: 2, sm: 3, md: 4 },
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: compact ? "none" : 1250,
            mx: "auto",
            px: compact ? 0 : { xs: 1.5, sm: 2.5, md: 3 },
            boxSizing: "border-box",
            minWidth: 0,
          }}
        >
          {showHeader && (
            <Paper
              elevation={0}
              sx={{
                p: compact
                  ? { xs: 1.25, sm: 1.5 }
                  : { xs: 2, sm: 3 },
                mb: compact ? 1.5 : 2.5,
                borderRadius: compact ? 2 : 3,
                color: "#fff",
                background:
                  "linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)",
                boxShadow: "0 8px 24px rgba(25, 118, 210, 0.18)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <ReceiptLongIcon />
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Single Bill
                  </Typography>
                  <Typography sx={{ fontSize: 13, opacity: 0.9 }}>
                    Create, save, print, and share an individual guest bill
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {(localError || guest.errorMsg) && (
            <Alert
              severity="error"
              sx={{ mb: 1.5, borderRadius: 2 }}
              onClose={() => {
                setLocalError("");
                guest.setErrorMsg("");
              }}
            >
              {localError || guest.errorMsg}
            </Alert>
          )}

          {guest.successMsg && (
            <Alert
              severity="success"
              sx={{ mb: 1.5, borderRadius: 2 }}
              onClose={() => guest.setSuccessMsg("")}
            >
              {guest.successMsg}
            </Alert>
          )}

          <Paper
            elevation={0}
            sx={{
              p: compact ? { xs: 1.25, sm: 1.5 } : { xs: 2, sm: 3 },
              mb: compact ? 1.5 : 2.5,
              borderRadius: compact ? 2 : 3,
              border: "1px solid #e2e8f0",
            }}
          >
            <Typography fontWeight={700} sx={{ mb: 1.5 }}>
              Hostel Details
            </Typography>

          <TextField
  select
  fullWidth
  size={compact ? "small" : "medium"}
  label="Select Hostel"
  value={guest.selectedHostel || ""}
  onChange={handleHostelChange}
  disabled={isExisting || isViewing || isEditing}
>
  {(guest.hostelList || []).map((hostel) => (
    <MenuItem key={hostel.id} value={hostel.hostelName}>
      {hostel.hostelName}
    </MenuItem>
  ))}
</TextField>

            {isExisting && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                The hostel cannot be changed for an existing bill.
              </Typography>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: compact ? { xs: 1, sm: 1.5 } : { xs: 1.5, sm: 2.5 },
              borderRadius: compact ? 2 : 3,
              border: "1px solid #e2e8f0",
              background: "#fff",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography fontWeight={700} color="#1e293b">
                Guest Bill Details
              </Typography>

              {isViewing && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                >
                  Edit
                </Button>
              )}

              {isEditing && (
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => {
                    const row = guest.rows[0];
                    if (row) handleSave(row);
                  }}
                >
                  Save Changes
                </Button>
              )}
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, mb: 2 }}
            >
              Enter guest information, stay or mess charges, payment details,
              and remarks.
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {guest.rows.length > 0 ? (
              guest.rows.map((row, index) => (
              <GuestEntryCard
  key={row.id || `single-${index}`}
  index={index}
  row={{
  ...row,
  isEditing:
    isCreating ||
    isEditing ||
    isDateOnlyEdit ||
    isFullOccupancyEdit,
}}
  hostelId={hostelId}
  roomTypes={guest.roomTypes}
  hostelRoomDTOs={guest.hostelRoomDTOs || []}
  rows={guest.rows}
  handleChange={guest.handleChange}
  handleSaveRow={handleSave}
  handlePrint={guest.handlePrint}
  handleWhatsApp={guest.handleWhatsApp}
  setRows={guest.setRows}
  setRowToDelete={guest.setRowToDelete}
  setOpenDeleteDialog={guest.setOpenDeleteDialog}
  isReadOnly={isViewing}
  isDateOnlyEdit={isDateOnlyEdit}
  isFullOccupancyEdit={isFullOccupancyEdit}
  showPrintActions={!isOccupancyEdit && isExisting}
  showWhatsAppActions={!isOccupancyEdit}
  showDeleteActions={!isOccupancyEdit}
/>
              ))
            ) : (
              <Box
                sx={{
                  py: 4,
                  px: 2,
                  textAlign: "center",
                  border: "1px dashed #cbd5e1",
                  borderRadius: 2,
                  background: "#f8fafc",
                }}
              >
                <ReceiptLongIcon
                  sx={{ fontSize: 36, color: "#94a3b8", mb: 1 }}
                />
                <Typography fontWeight={600} color="#475569">
                  No bill started
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Select a hostel, then start a new guest bill.
                </Typography>
              </Box>
            )}

            {isCreating && (
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={startNewBill}
                disabled={!guest.selectedHostel}
                sx={{
                  mt: 1.5,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: "none",
                }}
              >
                {guest.rows.length > 0
                  ? "Start Another Bill"
                  : "Create New Bill"}
              </Button>
            )}
          </Paper>
        </Box>
      </Box>

      {!isOccupancyEdit && (
  <DeleteDialog
    open={guest.openDeleteDialog}
    row={guest.rowToDelete}
    onClose={() => guest.setOpenDeleteDialog(false)}
    onConfirm={guest.confirmDelete}
  />
)}
    </>
  );
};

export default SingleBill;
